<!--
ChannelTemplateForm — edita o par (gatilho x tipo de canal) num idioma de cada
vez. O seletor de idioma no topo troca a VARIANTE que está sendo editada; um
idioma que ainda não existe é oferecido para criar aqui mesmo, copiando o
pt-BR. Não há mais um "modo criar" separado: criar variante é uma ação de
dentro do par, não uma tela que pergunta gatilho e canal de novo.

Três coisas acontecem aqui, e são deliberadamente distintas:

  1. SALVAR  — grava assunto/corpo. Valida no servidor (variável fora do
     catálogo do gatilho é recusada com nome).
  2. PRÉ-VISUALIZAR — renderiza a linha SALVA com os valores de exemplo do
     gatilho e não manda nada. Funciona com o gate de assinatura desligado.
  3. ENVIAR TESTE — manda de VERDADE a variante SELECIONADA, pelo canal
     configurado, para o endereço digitado. Respeita o gate de assinatura
     (chamado 2026-0043). O idioma do teste é o do seletor do topo: editar
     numa língua e testar em outra, sem perceber, seria fácil demais.

A prévia é do que está SALVO, não do rascunho: o endpoint lê o banco. Está
dito na tela para não parecer que a edição "não pegou".
-->
<template>
  <div class="channel-template-form" data-testid="channel-template-form">
    <!-- Seletor de idioma: troca a variante em edição. Idioma sem variante
         não some da lista — aparece marcado e oferece a criação. -->
    <div class="channel-template-langbar">
      <select-list
        v-model="selectedLanguage"
        elem-id="channel-template-language"
        :label="t('Idioma')"
        :itens-select="languageOptions"
        :auto-select-first="false"
        data-testid="channel-template-language"
      />
      <Button
        v-if="!currentTemplate"
        :label="t('Nova variante de idioma')"
        icon="pi pi-plus"
        size="small"
        severity="secondary"
        :disabled="creating"
        data-testid="channel-template-create-variant-btn"
        @click="handleCreateVariant"
      />
    </div>

    <p v-if="!currentTemplate" class="channel-template-hint" data-testid="channel-template-no-variant">
      {{ t('Ainda não existe versão deste template em') }} {{ selectedLanguage }}.
      {{ t('A variante nasce com o texto do pt-BR, para você traduzir por cima.') }}
    </p>

    <template v-if="currentTemplate">
      <p class="channel-template-vars" data-testid="channel-template-vars">
        {{ t('Variáveis disponíveis') }}: {{ allowedVarsDisplay }}
      </p>

      <input-text
        v-if="isEmail"
        v-model="subject"
        elem-id="channel-template-subject"
        :label="t('Assunto')"
        data-testid="channel-template-subject"
      />

      <input-text-area
        v-model="body"
        elem-id="channel-template-body"
        :label="t('Corpo da mensagem')"
        :ng-required="true"
        data-testid="channel-template-body"
      />

      <div class="channel-template-actions">
        <Button
          :label="t('Pré-visualizar')"
          icon="pi pi-eye"
          size="small"
          severity="secondary"
          outlined
          :disabled="previewing"
          data-testid="channel-template-preview-btn"
          @click="handlePreview"
        />
        <Button
          :label="showTestPanel ? t('Fechar teste') : t('Testar este template')"
          icon="pi pi-send"
          size="small"
          severity="secondary"
          outlined
          data-testid="channel-template-test-toggle"
          @click="showTestPanel = !showTestPanel"
        />
      </div>

      <div v-if="preview" class="channel-template-preview" data-testid="channel-template-preview-panel">
        <span class="channel-template-hint">
          {{ t('Prévia do template salvo, com valores de exemplo') }}
        </span>
        <span v-if="preview.subject" class="channel-template-preview-subject" data-testid="channel-template-preview-subject">
          {{ t('Assunto') }}: {{ preview.subject }}
        </span>
        <!--
          Corpo de e-mail chega como HTML já embrulhado pelo EmailLayout. Vai
          para um iframe com sandbox VAZIO: é marcação montada a partir de
          texto que o staff escreve, e não entra no DOM do console.
        -->
        <iframe
          v-if="preview.html"
          class="channel-template-preview-frame"
          sandbox=""
          :title="t('Prévia do e-mail')"
          :srcdoc="preview.body"
          data-testid="channel-template-preview-body"
        ></iframe>
        <pre v-else class="channel-template-preview-body" data-testid="channel-template-preview-body">{{ preview.body }}</pre>
      </div>

      <!-- ENVIO DE TESTE REAL -->
      <div v-if="showTestPanel" class="channel-template-test" data-testid="channel-template-test-panel">
        <strong>{{ t('Enviar teste') }}</strong>
        <p class="channel-template-hint">
          {{ t('Envio real da variante') }} {{ selectedLanguage }}{{ t(', pelo canal configurado. O nome da franquia escolhida entra na mensagem; as demais variáveis usam valores de exemplo.') }}
        </p>

        <franchise-select
          v-model="testFranquiaId"
          elem-id="channel-template-test-franchise"
          :label="t('Franquia')"
          data-testid="channel-template-test-franchise"
          @franchise-data="onFranchiseData"
        />

        <input-text
          v-model="testTarget"
          elem-id="channel-template-test-target"
          :label="isEmail ? t('E-mail de destino') : t('Telefone de destino')"
          data-testid="channel-template-test-target"
        />

        <!-- Cada impedimento diz o que fazer: os consertos são em telas
             diferentes (criar variante aqui x configurar canal na aba Canais). -->
        <p v-if="!testChannel" class="channel-template-error" data-testid="channel-template-test-no-channel">
          {{ t('Nenhum canal ativo deste tipo. Configure um na aba Canais.') }}
        </p>

        <Button
          :label="t('Enviar teste')"
          icon="pi pi-send"
          size="small"
          :disabled="!canSendTest || testing"
          data-testid="channel-template-test-send-btn"
          @click="handleTestSend"
        />

        <div v-if="testResult" class="channel-template-test-result" data-testid="channel-template-test-result">
          <span
            class="channel-template-test-badge"
            :class="testResult.delivered ? 'test-ok' : 'test-fail'"
            data-testid="channel-template-test-result-badge"
          >
            {{ testResult.delivered ? t('Entregue') : t('Falhou') }}
          </span>
          <pre class="channel-template-test-detail" data-testid="channel-template-test-result-detail">{{ testResult.detail }}</pre>
        </div>
      </div>
    </template>

    <p v-if="errorMessage" class="channel-template-error" data-testid="channel-template-error">
      {{ errorMessage }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { useFranchisorChannels } from '../../../composables/useFranchisorChannels'
import { FRANCHISOR_TRIGGER_LABELS } from './franchisorTriggerLabels'
import type { FranquiaData } from '../../../services/permissionsService'
import type { FranchisorChannels_TemplateRow } from '../../../contracts/FranchisorChannels_TemplateRow'
import type { FranchisorChannels_TriggerRow } from '../../../contracts/FranchisorChannels_TriggerRow'
import type { FranchisorChannels_PreviewResponseDados } from '../../../contracts/FranchisorChannels_PreviewResponse'
import type { FranchisorChannels_TestSendResponseDados } from '../../../contracts/FranchisorChannels_TestSendResponse'

const props = defineProps<{
  row: FranchisorChannels_TemplateRow | null
  triggers: FranchisorChannels_TriggerRow[]
}>()

const emit = defineEmits<{
  saved: []
  'validation-failed': []
}>()

const { t } = useI18n()
const {
  channels,
  templates,
  templateLanguages,
  saveTemplate,
  createTemplateVariant,
  previewTemplate,
  testSend,
} = useFranchisorChannels()

const DEFAULT_LANGUAGE = 'pt-BR'


/** O idioma em edição — troca a variante, não o par. */
const selectedLanguage = ref(DEFAULT_LANGUAGE)
const creating = ref(false)

/**
 * A variante do par no idioma selecionado, ou null quando ela ainda não
 * existe (é o que faz aparecer o botão de criar). Lê da lista do composable,
 * então uma criação recém-feita já é encontrada aqui sem recarregar a página.
 */
const currentTemplate = computed(() =>
  templates.value.find(
    (tpl) =>
      tpl.trigger_key === props.row?.trigger_key &&
      tpl.channel_type === props.row?.channel_type &&
      tpl.language === selectedLanguage.value,
  ) ?? null,
)

const isEmail = computed(() => props.row?.channel_type === 'email')

const subject = ref('')
const body = ref('')
const errorMessage = ref('')

const previewing = ref(false)
const preview = ref<FranchisorChannels_PreviewResponseDados | null>(null)

const showTestPanel = ref(false)
const testing = ref(false)
const testFranquiaId = ref<number | string | null>(null)
const testTarget = ref('')
const testResult = ref<FranchisorChannels_TestSendResponseDados | null>(null)

// ── Opções ────────────────────────────────────────────────────────────────

const languages = computed(() =>
  templateLanguages.value.length > 0 ? templateLanguages.value : [DEFAULT_LANGUAGE],
)

/**
 * TODOS os idiomas suportados, não só os que já existem: o que ainda não foi
 * traduzido precisa estar na lista para poder ser criado, e é marcado como
 * pendente em vez de simplesmente faltar.
 */
const languageOptions = computed(() => {
  const existing = new Set(
    templates.value
      .filter((tpl) => tpl.trigger_key === props.row?.trigger_key && tpl.channel_type === props.row?.channel_type)
      .map((tpl) => tpl.language),
  )
  return languages.value.map((l) => ({
    ID: l,
    Name: existing.has(l) ? l : `${l} — ${t('sem variante')}`,
  }))
})

const allowedVarsDisplay = computed(() => {
  const trigger = props.triggers.find((tr) => tr.trigger_key === props.row?.trigger_key)
  const vars = trigger?.vars ?? []
  return vars.length > 0 ? vars.map((v) => `{{${v}}}`).join(', ') : '-'
})

// ── Resolução do envio de teste ───────────────────────────────────────────

/**
 * O template que o teste vai mandar: mesmo par, no idioma ESCOLHIDO no painel
 * — que pode não ser o da linha aberta. Testar "o que o chileno recebe" sem
 * sair da tela é justamente o ponto.
 */
/** O primeiro canal ATIVO do tipo do template — é por ele que o teste sai. */
const testChannel = computed(
  () => channels.value.find((c) => c.type === props.row?.channel_type && c.enabled) ?? null,
)

const canSendTest = computed(
  () => currentTemplate.value !== null && testChannel.value !== null && testTarget.value.trim() !== '',
)

// ── Estado ────────────────────────────────────────────────────────────────

watch(
  () => props.row,
  (row) => {
    selectedLanguage.value = row?.language ?? DEFAULT_LANGUAGE
    testTarget.value = ''
    testFranquiaId.value = null
    showTestPanel.value = false
  },
  { immediate: true },
)

/**
 * O editor sempre reflete a variante selecionada. Trocar de idioma descarta a
 * prévia e o resultado do teste anterior de propósito: eles são daquele
 * idioma, e deixá-los na tela ao lado de outro texto seria uma resposta certa
 * para a pergunta errada.
 */
watch(
  currentTemplate,
  (tpl) => {
    subject.value = tpl?.subject ?? ''
    body.value = tpl?.body ?? ''
    errorMessage.value = ''
    preview.value = null
    testResult.value = null
  },
  { immediate: true },
)

/** O e-mail cadastrado da franquia é o destino mais provável — prefill, não trava. */
function onFranchiseData(data: FranquiaData | null | undefined): void {
  const email = String(data?.Dados?.Email ?? '')
  if (isEmail.value && email !== '' && testTarget.value.trim() === '') {
    testTarget.value = email
  }
}

const canSave = computed(() => currentTemplate.value !== null && body.value.trim() !== '')

// ── Ações ─────────────────────────────────────────────────────────────────

async function handlePreview(): Promise<void> {
  const template = currentTemplate.value
  if (!template) return
  previewing.value = true
  errorMessage.value = ''
  try {
    preview.value = await previewTemplate({ template_id: template.id })
  } catch (err) {
    preview.value = null
    errorMessage.value = (err as Error)?.message ?? ''
    log('ChannelTemplateForm', 'handlePreview', 'Erro ao pré-visualizar', errorMessage.value)
  } finally {
    previewing.value = false
  }
}

async function handleTestSend(): Promise<void> {
  const template = currentTemplate.value
  const channel = testChannel.value
  if (!template || !channel) return

  testing.value = true
  testResult.value = null
  errorMessage.value = ''
  try {
    testResult.value = await testSend({
      channel_id: channel.id,
      target: testTarget.value.trim(),
      template_id: template.id,
      ...(testFranquiaId.value != null ? { franquia_id: Number(testFranquiaId.value) } : {}),
    })
  } catch (err) {
    errorMessage.value = (err as Error)?.message ?? ''
    log('ChannelTemplateForm', 'handleTestSend', 'Erro ao enviar teste', errorMessage.value)
  } finally {
    testing.value = false
  }
}

/**
 * Cria a variante do idioma selecionado copiando o pt-BR. Depois disso
 * `currentTemplate` a encontra sozinho — o editor aparece já preenchido, sem
 * fechar o modal nem recarregar a aba.
 */
async function handleCreateVariant(): Promise<void> {
  if (!props.row) return
  creating.value = true
  errorMessage.value = ''
  try {
    await createTemplateVariant({
      trigger_key: props.row.trigger_key ?? '',
      channel_type: props.row.channel_type ?? '',
      language: selectedLanguage.value,
    })
    eventBus.emit('globalMessage.success', t('Variante criada'))
  } catch (err) {
    errorMessage.value = (err as Error)?.message ?? ''
    log('ChannelTemplateForm', 'handleCreateVariant', 'Erro ao criar variante', errorMessage.value)
  } finally {
    creating.value = false
  }
}

async function save(): Promise<void> {
  errorMessage.value = ''
  const template = currentTemplate.value
  if (!template || !canSave.value) {
    emit('validation-failed')
    return
  }

  await saveTemplate({
    id: template.id,
    body: body.value,
    ...(isEmail.value ? { subject: subject.value } : {}),
  })
  eventBus.emit('globalMessage.success', t('Template salvo'))

  emit('saved')
}

defineExpose({ canSave, save })
</script>

<style scoped>
.channel-template-form {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.channel-template-actions {
  display: flex;
  gap: 0.5rem;
}

.channel-template-vars,
.channel-template-hint {
  color: var(--p-text-muted-color, #6b7280);
  font-size: 0.78rem;
  margin: 0;
}

.channel-template-preview,
.channel-template-test {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--p-content-border-color, #e5e7eb);
  background: var(--p-content-hover-background, #f9fafb);
}

.channel-template-preview-subject {
  font-weight: 600;
  font-size: 0.85rem;
}

.channel-template-preview-frame {
  width: 100%;
  height: 320px;
  border: 1px solid var(--p-content-border-color, #e5e7eb);
  border-radius: 6px;
  background: #ffffff;
}

.channel-template-preview-body,
.channel-template-test-detail {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  font-size: 0.85rem;
}

.channel-template-test-badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
}

.test-ok {
  background: #dcfce7;
  color: #166534;
}

.test-fail {
  background: #fee2e2;
  color: #991b1b;
}

.channel-template-error {
  color: #991b1b;
  font-size: 0.78rem;
  margin: 0;
}
</style>
